//src/components/TimeLine.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, updateDoc, onSnapshot, arrayUnion, arrayRemove, Timestamp, getDocs, collection } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, auth, storage } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Khởi tạo Firebase Functions
const functions = getFunctions();

const Timeline = ({ teamId, refreshKey = 0 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [draggedTask, setDraggedTask] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetails, setShowTaskDetails] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTask, setEditedTask] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    priority: 'all',
    assignee: 'all',
    dueDate: 'all',
    progress: 'all'
  });
  const [sortOption, setSortOption] = useState('dueDate');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showSortModal, setShowSortModal] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [showAssigneeSelector, setShowAssigneeSelector] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [taskFiles, setTaskFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  // Biến trạng thái để quản lý thông báo
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'error'  // 'error', 'warning', 'info', 'success'
  });
  // Biến trạng thái cho xác nhận xóa tệp
  const [showFileDeleteConfirm, setShowFileDeleteConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  // Hàm tiện ích hiển thị thông báo
  const showNotification = (message, type = 'error') => {
    setNotification({
      show: true,
      message,
      type
    });
    
    // Tự động đóng thông báo sau 5 giây
    setTimeout(() => {
      setNotification(prev => ({...prev, show: false}));
    }, 5000);
  };

  // Hàm tiện ích đóng thông báo
  const closeNotification = () => {
    setNotification(prev => ({...prev, show: false}));
  };

  // Helper function to safely get a Date object from various timestamp formats
  const getDateFromTimestamp = (timestamp) => {
    if (!timestamp) return null;

    // Handle Firebase Timestamp objects
    if (timestamp && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
    }

    // Handle Firestore Timestamp object {seconds, nanoseconds}
    if (
      typeof timestamp === 'object' &&
      typeof timestamp.seconds === 'number' &&
      typeof timestamp.nanoseconds === 'number'
    ) {
      return new Date(timestamp.seconds * 1000 + Math.floor(timestamp.nanoseconds / 1e6));
    }

    // Handle ISO strings or numbers (milliseconds)
    try {
        const date = new Date(timestamp);
        // Check if the date is valid
        if (!isNaN(date.getTime())) {
            return date;
        }
    } catch (e) {
        console.error("Error parsing date timestamp:", timestamp, e);
        return null;
    }

    console.warn("Unrecognized timestamp format:", timestamp);
    return null; // Return null if format is unrecognized or invalid
  };

  // Function to format date and time
  const formatDateTime = (date) => {
    // First, get a valid Date object
    const d = getDateFromTimestamp(date);

    // If the date object is invalid or null, return 'N/A'
    if (!d) {
        console.error("Lỗi khi định dạng thời gian: Invalid date object received in formatDateTime", date);
        return 'N/A'; // Return 'N/A' or some other placeholder
    }

    // Proceed with formatting if the date object is valid
    try {
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        // const seconds = String(d.getSeconds()).padStart(2, '0'); // Optional: include seconds

        return `${day}/${month}/${year} ${hours}:${minutes}`;
        // return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`; // If including seconds
    } catch (error) {
        // Catch any unexpected errors during formatting
        console.error("Lỗi khi định dạng thời gian:", error);
        // Attempt to return the original input or a fallback string
        // This might help identify the problematic input value
        return String(date) || 'Lỗi định dạng';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = getDateFromTimestamp(date);
    if (!d) return 'N/A';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Hàm chuyển đổi role sang tiếng Việt
  const getRoleLabel = (role) => {
    if (!role) return '';
    switch (role) {
      case 'executor':
      case 'assignee':
      case 'Người thực hiện':
        return 'Người thực hiện';
      case 'collaborator':
      case 'Người phối hợp':
        return 'Người phối hợp';
      case 'supervisor':
      case 'Người giám sát':
        return 'Người giám sát';
      case 'approver':
      case 'Người phê duyệt':
        return 'Người phê duyệt';
      case 'member':
      case 'Thành viên':
        return 'Thành viên';
      default:
        return role;
    }
  };

  // Hàm lấy vai trò của currentUser trong task
  const getUserRoleInTask = (task, currentUser) => {
    if (!task || !currentUser) return null;
    if (Array.isArray(task.assignees)) {
      const found = task.assignees.find(a => a.uid === currentUser.uid);
      if (found && found.role) return getRoleLabel(found.role);
      if (found) return 'Người thực hiện';
    }
    if (Array.isArray(task.participants)) {
      const found = task.participants.find(a => a.uid === currentUser.uid);
      if (found && found.role) return getRoleLabel(found.role);
      if (found) return 'Thành viên';
    }
    if (task.approverId === currentUser?.uid) return 'Người phê duyệt';
    return null;
  };

  // Component hiển thị nhật ký hoạt động
  const ActivityLogItem = ({ log, defaultTaskName }) => {
    if (!log) return null;
    
    // Xử lý trường hợp log không có các trường cần thiết
    const timestamp = formatDateTime(log.timestamp);
    const userDisplayName = log.userDisplayName || 'Người dùng không xác định';
    
    // Hàm để xác định nội dung của nhật ký dựa vào loại
    const getActivityContent = () => {
      switch (log.type) {
        case 'status_change':
          return (
            <span>
              Thay đổi trạng thái từ{' '}
              <span className="font-medium">{log.details?.statusLabels?.from || 'Không xác định'}</span> sang{' '}
              <span className="font-medium">{log.details?.statusLabels?.to || 'Không xác định'}</span>
            </span>
          );
        case 'completion':
          return <span>Đánh dấu công việc là hoàn thành</span>;
        case 'approval':
          return <span>Đã phê duyệt công việc</span>;
        case 'rejection':
          return <span>Đã từ chối công việc</span>;
        case 'cancellation':
          return <span>Đã hủy công việc</span>;
        case 'comment_added':
          return (
            <span>
              Đã thêm bình luận:{' '}
              <span className="italic">"{log.details?.commentText || ''}"</span>
            </span>
          );
        case 'file_uploaded':
          return (
            <span>
              Đã tải lên tệp:{' '}
              <span>{log.details?.fileName || 'Tệp không xác định'}</span>
            </span>
          );
        case 'file_deleted':
          return (
            <span>
              Đã xóa tệp:{' '}
              <span>{log.details?.fileName || 'Tệp không xác định'}</span>
            </span>
          );
        case 'assignee_added':
          return (
            <span>
              Đã thêm người phụ trách:{' '}
              <span>{log.details?.added?.map(a => a.displayName).join(', ') || 'Không xác định'}</span>
            </span>
          );
        case 'assignee_removed':
          return (
            <span>
              Đã xóa người phụ trách:{' '}
              <span>{log.details?.removed?.map(a => a.displayName).join(', ') || 'Không xác định'}</span>
            </span>
          );
        case 'progress_update':
          return (
            <span>
              Đã cập nhật tiến độ từ {log.details?.from || 0}% sang {log.details?.to || 0}%
            </span>
          );
        case 'field_update':
          return (
            <span>
              Đã cập nhật:{' '}
              <span>{log.details?.changedFields?.join(', ') || 'không xác định'}</span>
            </span>
          );
        default:
          return <span>Đã cập nhật công việc</span>;
      }
    };
    
    // Just return the essential data needed for a table row
    return {
      timestamp,
      userDisplayName,
      userPhotoURL: log.userPhotoURL,
      content: getActivityContent(),
      taskName: log.taskName || defaultTaskName || 'Không xác định'
    };
  };

  // Hàm để chuyển đổi URL thành liên kết có thể nhấp được
const linkifyText = (text) => {
  if (!text) return [];
  
  // Biểu thức chính quy để nhận dạng URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  
  // Mảng kết quả để lưu giữ phần tử văn bản và liên kết
  const result = [];
  
  // Tìm và thay thế tất cả URL bằng phương pháp tốt hơn
  let lastIndex = 0;
  let match;
  
  // Lặp qua tất cả các URL được tìm thấy
  while ((match = urlRegex.exec(text)) !== null) {
    // Thêm văn bản trước URL
    if (match.index > lastIndex) {
      result.push(text.substring(lastIndex, match.index));
    }
    
    // Thêm URL dưới dạng liên kết có thể nhấp
    const url = match[0];
    result.push(
      <a 
        key={`link-${match.index}`}
        href={url} 
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-500 hover:underline"
      >
        {url}
      </a>
    );
    
    lastIndex = match.index + url.length;
  }
  
  // Thêm bất kỳ văn bản còn lại sau URL cuối cùng
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }
  
  return result;
};

  // Lấy danh sách công việc từ Firestore - Đã sửa để bao gồm phụ thuộc refreshKey
  useEffect(() => {
    const fetchTasks = async () => {
      if (!teamId) {
        setError('Không có ID phòng ban được cung cấp');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        if (teamId === 'all') {
          // System admin xem lịch của tất cả phòng ban
          const teamsSnapshot = await getDocs(collection(db, 'teams'));
          const allTasks = [];
          teamsSnapshot.forEach(teamDoc => {
            const data = teamDoc.data();
            if (Array.isArray(data.tasks)) {
              data.tasks.forEach(task => {
                const bgColor = getBgColorForPriority(task.priority);
                allTasks.push({ ...task, bgColor, teamId: teamDoc.id, teamName: data.name });
              });
            }
          });
          setTasks(allTasks);
        } else {
          const teamRef = doc(db, 'teams', teamId);
          const teamSnap = await getDoc(teamRef);
          if (teamSnap.exists()) {
            const teamData = teamSnap.data();
            if (teamData.tasks && Array.isArray(teamData.tasks)) {
              // Xử lý công việc để thêm màu nền dựa trên mức ưu tiên
              const processedTasks = teamData.tasks.map(task => {
                const bgColor = getBgColorForPriority(task.priority);
                return {
                  ...task,
                  bgColor
                };
              });
              
              setTasks(processedTasks);
            } else {
              setTasks([]);
            }
          } else {
            setError('Không tìm thấy phòng ban');
          }
        }
      } catch (err) {
        console.error('Lỗi khi lấy danh sách công việc:', err);
        setError(`Lỗi khi lấy danh sách công việc: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [teamId, refreshKey]);

  // Xử lý taskId từ query parameter để tự động mở modal
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskIdFromQuery = params.get('taskId');
    
    if (taskIdFromQuery && tasks.length > 0) {
      const foundTask = tasks.find(task => {
        // So sánh cả string và number để đảm bảo match
        const taskIdStr = String(task.id);
        const queryIdStr = String(taskIdFromQuery);
        return taskIdStr === queryIdStr;
      });
      
      if (foundTask) {
        setSelectedTask(foundTask);
        setShowTaskDetails(true);
      }
    }
  }, [location.search, tasks]);

  // Lấy vai trò hiện tại của người dùng trong phòng ban
  useEffect(() => {
    const fetchUserRole = async () => {
      if (!teamId) return;
      
      try {
        // Sử dụng trực tiếp đối tượng auth đã nhập, thay vì gọi getAuth()
        const user = auth.currentUser;
        
        if (user) {
          console.log("Người dùng hiện tại:", user.uid, user.email);
          setCurrentUser(user);
          
          // Lấy dữ liệu phòng ban để kiểm tra vai trò người dùng
          const teamRef = doc(db, 'teams', teamId);
          const teamSnap = await getDoc(teamRef);
          
          if (teamSnap.exists()) {
            const teamData = teamSnap.data();
            console.log("Dữ liệu phòng ban:", teamData);
            console.log("Dữ liệu thành viên:", teamData.membersData);
            
            if (teamData.membersData && Array.isArray(teamData.membersData)) {
              // Tìm người dùng hiện tại trong mảng
              const memberData = teamData.membersData.find(member => member.uid === user.uid);
              if (memberData) {
                const role = memberData.role;
                console.log("Đã tìm thấy vai trò người dùng trong mảng:", role);
                setUserRole(role);
              } else {
                console.log("Không tìm thấy người dùng trong mảng thành viên phòng ban");
              }
            } else if (teamData.membersData && typeof teamData.membersData === 'object') {
              // Giữ lại logic tìm kiếm trong đối tượng làm phương án dự phòng
              if (teamData.membersData[user.uid]) {
                const role = teamData.membersData[user.uid].role;
                console.log("Đã tìm thấy vai trò người dùng trong đối tượng:", role);
                setUserRole(role);
              } else {
                console.log("Không tìm thấy người dùng trong đối tượng thành viên phòng ban");
              }
            } else {
              console.log("Không tìm thấy cấu trúc dữ liệu membersData hợp lệ");
            }
          } else {
            console.log("Không tìm thấy phòng ban");
          }
        } else {
          console.log("Không có người dùng nào đã đăng nhập");
        }
      } catch (err) {
        console.error('Lỗi khi lấy vai trò người dùng:', err);
      }
    };
    
    fetchUserRole();
  }, [teamId]);

  // Lấy danh sách thành viên phòng ban
  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!teamId) return;
      
      try {
        const teamRef = doc(db, 'teams', teamId);
        const teamSnap = await getDoc(teamRef);
        
        if (teamSnap.exists()) {
          const teamData = teamSnap.data();
          if (teamData.membersData && Array.isArray(teamData.membersData)) {
            setTeamMembers(teamData.membersData);
          }
        }
      } catch (err) {
        console.error('Lỗi khi lấy thành viên phòng ban:', err);
      }
    };
    
    fetchTeamMembers();
  }, [teamId]);

  // Khởi tạo người được giao việc
  useEffect(() => {
    if (editedTask && editedTask.assignees) {
      setSelectedAssignees(editedTask.assignees);
    } else {
      setSelectedAssignees([]);
    }
  }, [editedTask]);

  // Lấy bình luận khi chi tiết công việc được mở
  useEffect(() => {
    const fetchComments = async () => {
      if (!selectedTask) return;
      
      try {
        const teamRef = doc(db, 'teams', teamId);
        const teamSnap = await getDoc(teamRef);
        
        if (teamSnap.exists()) {
          const teamData = teamSnap.data();
          // Lấy bình luận công việc, nếu không có thì đặt thành mảng rỗng
          const taskComments = teamData.tasks.find(t => t.id === selectedTask.id)?.comments || [];
          // Đảm bảo comments luôn là một mảng
          setComments(Array.isArray(taskComments) ? taskComments : []);
        }
      } catch (err) {
        console.error('Lỗi khi lấy bình luận:', err);
        // Đặt comments thành mảng rỗng nếu có lỗi
        setComments([]);
      }
    };
    
    if (selectedTask && showTaskDetails) {
      fetchComments();
    }
  }, [selectedTask, showTaskDetails, teamId]);

  // Lấy danh sách tệp đính kèm khi chi tiết công việc được mở
  useEffect(() => {
    const fetchTaskFiles = async () => {
      if (!selectedTask) return;
      
      try {
        const teamRef = doc(db, 'teams', teamId);
        const teamSnap = await getDoc(teamRef);
        
        if (teamSnap.exists()) {
          const teamData = teamSnap.data();
          const files = teamData.tasks.find(t => t.id === selectedTask.id)?.files || [];
          setTaskFiles(files);
        }
      } catch (err) {
        console.error('Lỗi khi lấy tệp đính kèm:', err);
      }
    };
    
    if (selectedTask && showTaskDetails) {
      fetchTaskFiles();
    }
  }, [selectedTask, showTaskDetails, teamId]);

  // Helper to manage completedAt logic and call the cloud function
  const updateTaskStatusAndDetailsAsync = async (taskId, taskDataChanges) => {
    const dataForCloudFunction = { ...taskDataChanges };

    if (dataForCloudFunction.status === 'completed') {
      dataForCloudFunction.completedAt = new Date(); 
    } else {
      const currentTask = tasks.find(t => t.id === taskId);
      if (currentTask && currentTask.completedAt) {
        dataForCloudFunction.completedAt = null; 
      } else if (dataForCloudFunction.hasOwnProperty('completedAt') && dataForCloudFunction.status !== 'completed') {
        dataForCloudFunction.completedAt = null;
      }
    }

    try {
      const updateTaskFunction = httpsCallable(functions, 'updateTask');
      await updateTaskFunction({
        teamId: teamId,
        taskId: taskId,
        taskData: dataForCloudFunction
      });

      const updatedCompletedAt = dataForCloudFunction.completedAt instanceof Date ? Timestamp.fromDate(dataForCloudFunction.completedAt) : (dataForCloudFunction.completedAt === null ? null : undefined);

      setTasks(prevTasks =>
        prevTasks.map(task =>
          task.id === taskId ? { ...task, ...dataForCloudFunction, completedAt: updatedCompletedAt } : task
        )
      );
      if (selectedTask && selectedTask.id === taskId) {
        setSelectedTask(prev => ({ ...prev, ...dataForCloudFunction, completedAt: updatedCompletedAt }));
      }
      if (editedTask && editedTask.id === taskId) {
        setEditedTask(prev => ({ ...prev, ...dataForCloudFunction, completedAt: updatedCompletedAt }));
      }
      showNotification('Cập nhật công việc thành công!', 'success');
      return true;
    } catch (err) {
      console.error('Lỗi khi cập nhật công việc:', err);
      showNotification('Không thể cập nhật công việc. Vui lòng thử lại.', 'error');
      return false;
    }
  };

  // Xử lý sự kiện kéo và thả
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
  };

  const handleDragOver = (e) => {
    e.preventDefault(); 
  };

  const handleDrop = async (e, newStatus) => { 
    e.preventDefault();
    if (draggedTask) {
      const isAssignee = draggedTask.assignees &&
        draggedTask.assignees.some(assignee => assignee.uid === currentUser?.uid);

      if (!isAssignee && userRole !== 'admin' && userRole !== 'manager') {
        showNotification('Chỉ người được giao việc, quản lý hoặc admin mới có thể cập nhật trạng thái công việc bằng kéo thả.', 'warning');
        setDraggedTask(null);
        return;
      }
      
      await updateTaskStatusAndDetailsAsync(draggedTask.id, { status: newStatus });
      setDraggedTask(null);
    }
  };
  
  const openTaskDetails = (task, e) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedTask(task);
    setShowTaskDetails(true);
    setIsEditing(false);
  };

  const closeTaskDetails = () => {
    setShowTaskDetails(false);
    setSelectedTask(null);
    setIsEditing(false);
    setEditedTask(null);
    
    const urlParams = new URLSearchParams(location.search);
    const fromDashboard = urlParams.get('from');
    if (fromDashboard === 'dashboard') {
      navigate('/dashboard');
    }
  };

  const isTaskAssignee = (taskIdToCheck) => { // Added taskIdToCheck parameter
    const task = taskIdToCheck ? (tasks.find(t => t.id === taskIdToCheck) || selectedTask) : selectedTask;
    if (!task || !currentUser) return false;
    return task.assignees && 
           task.assignees.some(assignee => assignee.uid === currentUser.uid);
  };
  
  const canEditTask = () => {
    if (!selectedTask || !currentUser) return false;
    if (isTaskAssignee(selectedTask.id)) return true;
    return userRole === 'admin' || userRole === 'manager';
  };
  
  const canUpdateStatusAndProgress = () => {
    if (!selectedTask || !currentUser) return false; // Ensure selectedTask is defined
    return isTaskAssignee(selectedTask.id);
  };

  const handleEditTask = () => {
    if (!selectedTask) return;
    if (!isTaskAssignee(selectedTask.id) && userRole !== 'admin' && userRole !== 'manager') {
      showNotification('Bạn không có quyền chỉnh sửa công việc này. Chỉ người được giao việc, quản lý phòng ban hoặc quản trị viên mới có thể chỉnh sửa công việc.', 'error');
      return;
    }
    setEditedTask({...selectedTask});
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedTask(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedTask(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePriorityChange = (priority) => {
    setEditedTask(prev => ({
      ...prev,
      priority
    }));
  };

  // Called when status is changed from the Task Details Modal's dropdown
  const handleModalStatusChange = async (newStatus) => {
    const taskToUpdate = editedTask || selectedTask;

    if (!taskToUpdate) {
      showNotification('Không có công việc nào được chọn để cập nhật trạng thái.', 'error');
      return;
    }
    
    const currentIsAssignee = taskToUpdate.assignees &&
      taskToUpdate.assignees.some(assignee => assignee.uid === currentUser?.uid);

    if (!currentIsAssignee && userRole !== 'admin' && userRole !== 'manager') {
      showNotification('Chỉ người được giao việc, quản lý hoặc admin mới có thể cập nhật trạng thái công việc.', 'warning');
      return;
    }

    if (editedTask && editedTask.id === taskToUpdate.id) {
      setEditedTask(prev => ({
        ...prev,
        status: newStatus,
      }));
      // For immediate persistence when status changes in modal even if other fields are being edited:
      await updateTaskStatusAndDetailsAsync(taskToUpdate.id, { status: newStatus });
    } else if (selectedTask && selectedTask.id === taskToUpdate.id && !isEditing) {
      // If not in edit mode, update immediately
      await updateTaskStatusAndDetailsAsync(selectedTask.id, { status: newStatus });
    }
  };

  const handleProgressChange = (e) => {
    if (!selectedTask && !editedTask) return; // Check if a task is selected/edited
    const taskForProgressUpdate = editedTask || selectedTask;

    if (!isTaskAssignee(taskForProgressUpdate.id) && userRole !== 'admin' && userRole !== 'manager') {
        showNotification('Chỉ người được giao việc, quản lý hoặc admin mới có thể cập nhật tiến độ công việc.', 'warning');
        return;
    }
    
    const progress = parseInt(e.target.value) || 0;
    setEditedTask(prevState => ({
      ...prevState,
      progress: Math.min(100, Math.max(0, progress))
    }));
  };

  const handleSaveChanges = async () => {
    if (!editedTask) return;

    setIsSaving(true);
    
    const taskDataForSave = { ...editedTask };

    if (taskDataForSave.status === 'completed') {
      taskDataForSave.completedAt = taskDataForSave.completedAt instanceof Date ? taskDataForSave.completedAt : (taskDataForSave.completedAt ? Timestamp.fromDate(new Date(taskDataForSave.completedAt)) : new Date());
    } else {
      delete taskDataForSave.completedAt;
    }
    
    try {
      const updateTaskFunction = httpsCallable(functions, 'updateTask');
      const { start_date: _removedStartDate, id: taskId, ...finalData } = taskDataForSave; // Ensure id is not nested in finalData if not needed by cloud function like that

      await updateTaskFunction({
        teamId: teamId,
        taskId: taskId, // Pass taskId directly
        taskData: finalData 
      });
      
      const updatedCompletedAt = finalData.completedAt instanceof Date ? Timestamp.fromDate(finalData.completedAt) : (finalData.completedAt === null ? null : undefined);

      setTasks(prevTasks =>
        prevTasks.map(task =>
          task.id === taskId ? { ...task, ...finalData, id: taskId, completedAt: updatedCompletedAt } : task
        )
      );
      setSelectedTask(prev => ({ ...prev, ...finalData, id: taskId, completedAt: updatedCompletedAt }));
      setIsEditing(false);
      showNotification('Đã cập nhật công việc thành công', 'success');
    } catch (err) {
      console.error('Lỗi khi cập nhật công việc:', err);
      showNotification('Không thể cập nhật công việc. Vui lòng thử lại.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const getBgColorForPriority = (priority) => {
    if (priority === 'high') return 'bg-red-400';
    if (priority === 'medium') return 'bg-yellow-400';
    if (priority === 'low') return 'bg-emerald-400';
    return 'bg-blue-500'; // Mặc định
  };

  // Bộ lọc trạng thái và logic lọc
  const statusFilters = [
    { label: 'Tất cả công việc', value: 'all' },
    { label: 'Chưa bắt đầu', value: 'notStarted' },
    { label: 'Đang thực hiện', value: 'inProgress' },
    { label: 'Chờ phê duyệt', value: 'pendingApproval' },
    { label: 'Đã phê duyệt', value: 'approved' },
    { label: 'Hoàn thành', value: 'completed' },
    { label: 'Đã hủy', value: 'canceled' },
    { label: 'Quá hạn', value: 'overdue' },
  ];

  // Cập nhật hàm lọc công việc
  const getFilteredTasks = () => {
    return tasks.filter((task) => {
      // Áp dụng bộ lọc trạng thái trước tiên
      let matchesStatus = true;
      if (selectedStatus !== 'all') {
        if (selectedStatus === 'overdue') {
          // Kiểm tra xem công việc có quá hạn không
          const dueDate = task.start_date && typeof task.start_date.toDate === 'function' 
            ? task.start_date.toDate() : new Date(task.start_date);
          const today = new Date();
          matchesStatus = dueDate < today && task.status !== 'completed';
        } else {
          matchesStatus = task.status === selectedStatus;
        }
      }
      
      // Áp dụng bộ lọc văn bản tìm kiếm
      const matchesSearch = task.text.toLowerCase().includes(searchText.toLowerCase()) || 
                           (task.description && task.description.toLowerCase().includes(searchText.toLowerCase()));
      
      // Áp dụng bộ lọc mức ưu tiên
      const matchesPriority = filterOptions.priority === 'all' || task.priority === filterOptions.priority;
      
      // Áp dụng bộ lọc người được giao việc
      const matchesAssignee = filterOptions.assignee === 'all' || 
                             (task.assignees && task.assignees.some(assignee => 
                               assignee.uid === filterOptions.assignee || assignee.email === filterOptions.assignee));
      
      // Áp dụng bộ lọc ngày hết hạn
      let matchesDueDate = true;
      if (filterOptions.dueDate === 'overdue') {
        const dueDate = task.start_date && typeof task.start_date.toDate === 'function' 
          ? task.start_date.toDate() : new Date(task.start_date);
        matchesDueDate = dueDate < new Date();
      } else if (filterOptions.dueDate === 'today') {
        const dueDate = task.start_date && typeof task.start_date.toDate === 'function' 
          ? task.start_date.toDate() : new Date(task.start_date);
        const today = new Date();
        matchesDueDate = dueDate.getDate() === today.getDate() && 
                        dueDate.getMonth() === today.getMonth() && 
                        dueDate.getFullYear() === today.getFullYear();
      } else if (filterOptions.dueDate === 'thisWeek') {
        const dueDate = task.start_date && typeof task.start_date.toDate === 'function' 
          ? task.start_date.toDate() : new Date(task.start_date);
        const today = new Date();
        const thisWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7);
        matchesDueDate = dueDate >= today && dueDate <= thisWeek;
      }
      
      // Áp dụng bộ lọc tiến độ
      let matchesProgress = true;
      if (filterOptions.progress === 'notStarted') {
        matchesProgress = task.progress === 0;
      } else if (filterOptions.progress === 'inProgress') {
        matchesProgress = task.progress > 0 && task.progress < 100;
      } else if (filterOptions.progress === 'completed') {
        matchesProgress = task.progress === 100;
      }
      
      return matchesStatus && matchesSearch && matchesPriority && matchesAssignee && matchesDueDate && matchesProgress;
    });
  };

  // Sắp xếp danh sách công việc
  const getSortedTasks = (filteredTasks) => {
    return [...filteredTasks].sort((a, b) => {
      let comparison = 0;
      
      if (sortOption === 'dueDate') {
        // Calculate due date from start_date + duration
        const calculateDueDate = (startDate, duration) => {
          if (!startDate) return new Date();
          const start = startDate && typeof startDate.toDate === 'function' 
            ? startDate.toDate() : new Date(startDate);
          return new Date(start.getTime() + (duration || 0) * 24 * 60 * 60 * 1000);
        };
        
        const dueDateA = calculateDueDate(a.start_date, a.duration);
        const dueDateB = calculateDueDate(b.start_date, b.duration);
        comparison = dueDateA - dueDateB;
      } else if (sortOption === 'priority') {
        const priorityValues = { high: 3, medium: 2, low: 1 };
        comparison = priorityValues[b.priority] - priorityValues[a.priority];
      } else if (sortOption === 'progress') {
        comparison = a.progress - b.progress;
      } else if (sortOption === 'title') {
        comparison = a.text.localeCompare(b.text);
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  // Sử dụng hàm lọc và sắp xếp
  const filteredTasks = getFilteredTasks();
  const sortedAndFilteredTasks = getSortedTasks(filteredTasks);

  // Đặt lại bộ lọc
  const resetFilters = () => {
    setFilterOptions({
      priority: 'all',
      assignee: 'all',
      dueDate: 'all',
      progress: 'all'
    });
  };

  // Xử lý thay đổi bộ lọc
  const handleFilterChange = (filterName, value) => {
    setFilterOptions(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Xử lý thay đổi sắp xếp
  const handleSortChange = (option) => {
    if (sortOption === option) {
      // Nếu nhấp vào tùy chọn sắp xếp hiện tại, đảo ngược hướng sắp xếp
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Nếu không, thay đổi tùy chọn sắp xếp và đặt thành tăng dần
      setSortOption(option);
      setSortDirection('asc');
    }
  };

  // Lấy nhãn trạng thái từ giá trị
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'notStarted':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'inProgress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'pendingApproval':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'approved':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'canceled':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'overdue':
        return 'bg-red-200 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'notStarted':
        return 'Chưa bắt đầu';
      case 'inProgress':
        return 'Đang thực hiện';
      case 'pendingApproval':
        return 'Chờ phê duyệt';
      case 'approved':
        return 'Đã phê duyệt';
      case 'completed':
        return 'Hoàn thành';
      case 'canceled':
        return 'Đã hủy';
      case 'overdue':
        return 'Quá hạn';
      default:
        return 'Chưa xác định';
    }
  };

  // Kiểm tra xem người dùng có thể chỉnh sửa công việc không
  const canEditTasks = userRole === 'admin' || userRole === 'manager' || isTaskAssignee();

  // Xử lý xóa công việc
  const handleDeleteTask = async () => {
    if (!selectedTask) return;
    
    // Hiển thị hộp thoại xác nhận tùy chỉnh
    setTaskToDelete(selectedTask);
    setShowDeleteConfirm(true);
  };

  // Xử lý xác nhận xóa
  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    
    try {
      setIsSaving(true);
      
      // Sử dụng cloud function để xóa công việc
      const deleteTaskFunction = httpsCallable(functions, 'deleteTask');
      const result = await deleteTaskFunction({
        teamId: teamId,
        taskId: taskToDelete.id
      });
      
      console.log('Xóa công việc thành công:', result.data);
      
      // Cập nhật trạng thái cục bộ
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskToDelete.id));
      
      // Đóng modal và hộp thoại xác nhận
      closeTaskDetails();
      setShowDeleteConfirm(false);
      setTaskToDelete(null);
      
      showNotification('Đã xóa công việc thành công', 'success');
    } catch (err) {
      console.error('Lỗi khi xóa công việc:', err);
      showNotification('Không thể xóa công việc. Vui lòng thử lại.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Hàm xử lý hủy xóa
const cancelDeleteTask = () => {
  setShowDeleteConfirm(false);
  setTaskToDelete(null);
};

// Xử lý thay đổi người phụ trách
const handleAssigneeChange = (member) => {
  setSelectedAssignees(prev => {
    // Kiểm tra xem thành viên đã được chọn chưa
    const isAlreadySelected = prev.some(assignee => assignee.uid === member.uid);
    
    if (isAlreadySelected) {
      // Nếu đã chọn, hãy xóa
      return prev.filter(assignee => assignee.uid !== member.uid);
    } else {
      // Nếu chưa chọn, hãy thêm vào
      return [...prev, member];
    }
  });
};

// Lưu người phụ trách đã chọn
const saveAssignees = () => {
  setEditedTask(prev => ({
    ...prev,
    assignees: selectedAssignees
  }));
  setShowAssigneeSelector(false);
};

// Hàm xử lý thêm bình luận
const handleAddComment = async () => {
  if (!newComment.trim() || !currentUser || !selectedTask) return;
  
  try {
    setIsSubmittingComment(true);
    
    const teamRef = doc(db, 'teams', teamId);
    const teamSnap = await getDoc(teamRef);
    
    if (teamSnap.exists()) {
      const teamData = teamSnap.data();
      const taskIndex = teamData.tasks.findIndex(t => t.id === selectedTask.id);
      
      if (taskIndex !== -1) {
        // Tạo đối tượng bình luận mới
        const newCommentObj = {
          id: Date.now().toString(),
          text: newComment.trim(),
          createdBy: {
            uid: currentUser.uid,
            displayName: currentUser.displayName || currentUser.email,
            email: currentUser.email
          },
          createdAt: new Date(),
          replyTo: replyTo
        };
        
        // Cập nhật trạng thái cục bộ
        const updatedComments = [...comments, newCommentObj];
        setComments(updatedComments);
        
        // Cập nhật Firestore
        const updatedTasks = [...teamData.tasks];
        if (!updatedTasks[taskIndex].comments) {
          updatedTasks[taskIndex].comments = [];
        }
        updatedTasks[taskIndex].comments.push(newCommentObj);
        
        await updateDoc(teamRef, {
          tasks: updatedTasks
        });
        
        // Gửi thông báo
        try {
          const sendNotificationFunction = httpsCallable(functions, 'sendNotification');
          
          // Các logic thông báo khác nhau
          if (replyTo) {
            // Trường hợp trả lời bình luận: gửi thông báo cho người được trả lời
            if (replyTo.createdBy && replyTo.createdBy.uid && replyTo.createdBy.uid !== currentUser.uid) {
              await sendNotificationFunction({
                userId: replyTo.createdBy.uid,
                title: 'Trả Lời Bình Luận Mới',
                message: `${currentUser.displayName || currentUser.email} đã trả lời bình luận của bạn trên công việc "${selectedTask.text}"`,
                type: 'comment_reply',
                teamId: teamId,
                teamName: teamData.name,
                taskId: selectedTask.id,
                taskName: selectedTask.text
              });
              showNotification(`Thông báo đã được gửi đến tác giả bình luận: ${replyTo.createdBy.displayName || replyTo.createdBy.email}`, 'success');
            }
          } else {
            // Trường hợp bình luận mới: thông báo cho tất cả người phụ trách công việc
            if (selectedTask.assignees && selectedTask.assignees.length > 0) {
              const notificationPromises = selectedTask.assignees
                .filter(assignee => assignee.uid !== currentUser.uid) // Không gửi thông báo cho chính mình
                .map(assignee => 
                  sendNotificationFunction({
                    userId: assignee.uid,
                    title: 'Bình Luận Mới Trên Công Việc',
                    message: `${currentUser.displayName || currentUser.email} đã bình luận trên công việc "${selectedTask.text}" mà bạn được phân công`,
                    type: 'task_comment',
                    teamId: teamId,
                    teamName: teamData.name,
                    taskId: selectedTask.id,
                    taskName: selectedTask.text
                  })
                );
              
              await Promise.all(notificationPromises);
              
              if (selectedTask.assignees.some(assignee => assignee.uid !== currentUser.uid)) {
                showNotification('Thông báo đã được gửi đến tất cả người phụ trách công việc', 'success');
              }
            }
          }
        } catch (notificationError) {
          console.error('Không thể gửi thông báo:', notificationError);
          // Không chặn việc thêm bình luận, chỉ ghi lại lỗi thông báo
        }
        
        // Đặt lại biểu mẫu
        setNewComment('');
        setReplyTo(null);
      }
    }
  } catch (err) {
    console.error('Lỗi khi thêm bình luận:', err);
    showNotification('Không thể thêm bình luận. Vui lòng thử lại.', 'error');
  } finally {
    setIsSubmittingComment(false);
  }
};

// Thiết lập mục tiêu trả lời
const handleReply = (comment) => {
  setReplyTo(comment);
  // Tập trung vào ô nhập bình luận
  document.getElementById('comment-input').focus();
};

// Hủy trả lời
const cancelReply = () => {
  setReplyTo(null);
};

// Xử lý tải lên tệp
const handleFileUpload = async (e) => {
  const file = e.target.files[0];
  if (!file) return;
  
  // Kiểm tra kích thước tệp (giới hạn 20MB)
  if (file.size > 20 * 1024 * 1024) {
    setUploadError('Kích thước tệp vượt quá giới hạn 20MB');
    return;
  }
  console.log('User before upload:', auth.currentUser); // Thêm dòng này
  if (!auth.currentUser) {
     showNotification('Người dùng chưa đăng nhập!', 'error');
     return; // Dừng nếu chưa đăng nhập
  }
  try {
    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    
    // Tạo đường dẫn tệp duy nhất
    const fileId = Date.now().toString();
    const fileExtension = file.name.split('.').pop();
    const filePath = `teams/${teamId}/tasks/${selectedTask.id}/${fileId}.${fileExtension}`;
    const fileRef = storageRef(storage, filePath);
    
    // Tải tệp lên
    const uploadTask = uploadBytesResumable(fileRef, file);
    
    // Theo dõi tiến trình tải lên
    uploadTask.on('state_changed', 
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => {
        console.error('Lỗi tải lên:', error);
        
        // Cung cấp thông báo lỗi cụ thể
        if (error.code === 'storage/unauthorized') {
          setUploadError('Quyền bị từ chối. Bạn có thể không có quyền tải lên tệp vào vị trí này.');
        } else {
          setUploadError(`Không thể tải lên tệp: ${error.message}`);
        }
        
        setIsUploading(false);
      },
      async () => {
        // Tải lên hoàn tất, lấy URL tải xuống
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        
        // Tạo đối tượng tệp
        const fileObject = {
          id: fileId,
          name: file.name,
          type: file.type,
          size: file.size,
          path: filePath,
          url: downloadURL,
          uploadedBy: {
            uid: currentUser.uid,
            displayName: currentUser.displayName || currentUser.email,
            email: currentUser.email
          },
          uploadedAt: new Date()
        };
        
        // Cập nhật Firestore
        const teamRef = doc(db, 'teams', teamId);
        const teamSnap = await getDoc(teamRef);
        
        if (teamSnap.exists()) {
          const teamData = teamSnap.data();
          const taskIndex = teamData.tasks.findIndex(t => t.id === selectedTask.id);
          
          if (taskIndex !== -1) {
            const updatedTasks = [...teamData.tasks];
            if (!updatedTasks[taskIndex].files) {
              updatedTasks[taskIndex].files = [];
            }
            updatedTasks[taskIndex].files.push(fileObject);
            
            await updateDoc(teamRef, {
              tasks: updatedTasks
            });
            
            // Cập nhật trạng thái cục bộ
            setTaskFiles(prev => [...prev, fileObject]);
          }
        }
        
        setIsUploading(false);
        setUploadProgress(0);
      }
    );
  } catch (err) {
    console.error('Lỗi xử lý tải lên tệp:', err);
    setUploadError('Không thể tải lên tệp. Vui lòng thử lại.');
    setIsUploading(false);
  }
};

// Mở hộp thoại xác nhận xóa tệp
const handleDeleteFile = (file) => {
  if (!file || !selectedTask) return;
  
  // Kiểm tra xem có quyền xóa tệp không (là người phụ trách công việc hoặc người tải lên tệp)
  const isAssignee = isTaskAssignee();
  const isUploader = file.uploadedBy && currentUser && file.uploadedBy.uid === currentUser.uid;
  
  if (!isAssignee && !isUploader) {
    showNotification('Chỉ người phụ trách công việc hoặc người đã tải lên tệp mới có thể xóa nó.', 'error');
    return;
  }
  
  // Hiển thị hộp thoại xác nhận
  setFileToDelete(file);
  setShowFileDeleteConfirm(true);
};

// Hàm xác nhận xóa tệp
const confirmDeleteFile = async () => {
  if (!fileToDelete) return;
  
  try {
    setIsSaving(true);
    
    // Xóa tệp
    const fileRef = storageRef(storage, fileToDelete.path);
    await deleteObject(fileRef);
    
    // Cập nhật danh sách tệp công việc
    const updatedFiles = taskFiles.filter(f => f.path !== fileToDelete.path);
    setTaskFiles(updatedFiles);
    
    // Cập nhật tham chiếu trong tài liệu Firestore
    const teamRef = doc(db, 'teams', teamId);
    const teamSnap = await getDoc(teamRef);
    
    if (teamSnap.exists()) {
      const teamData = teamSnap.data();
      const taskIndex = teamData.tasks.findIndex(t => t.id === selectedTask.id);
      
      if (taskIndex !== -1) {
        const updatedTasks = [...teamData.tasks];
        if (!updatedTasks[taskIndex].files) {
          updatedTasks[taskIndex].files = [];
        }
        
        // Xóa tệp khỏi danh sách
        updatedTasks[taskIndex].files = updatedTasks[taskIndex].files.filter(
          f => f.path !== fileToDelete.path
        );
        
        await updateDoc(teamRef, {
          tasks: updatedTasks
        });
        
        // Hiển thị thông báo thành công
        showNotification(`Tệp "${fileToDelete.name}" đã được xóa thành công`, 'success');
      }
    }
  } catch (err) {
    console.error('Lỗi khi xóa tệp:', err);
    showNotification('Không thể xóa tệp. Vui lòng thử lại.', 'error');
  } finally {
    setIsSaving(false);
    // Đóng hộp thoại xác nhận
    setShowFileDeleteConfirm(false);
    setFileToDelete(null);
  }
};

// Hủy xóa tệp
const cancelFileDelete = () => {
  setShowFileDeleteConfirm(false);
  setFileToDelete(null);
};

// Định dạng kích thước tệp
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Lấy biểu tượng tệp
const getFileIcon = (fileType) => {
  // Add check for valid fileType string
  if (typeof fileType !== 'string') {
    // Return a default icon if fileType is not a string
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  }

  if (fileType.includes('image')) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    );
  } else if (fileType.includes('pdf')) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  } else if (fileType.includes('word') || fileType.includes('document')) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  } else if (fileType.includes('excel') || fileType.includes('sheet')) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  } else {
    // Default icon for other types
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  }
};

// Hàm tính toán ngày đến hạn
const calculateDueDate = (startDate, duration) => {
  if (!startDate) return null;
  
  // Đảm bảo chúng ta có một đối tượng Date JavaScript
  const start = startDate instanceof Date 
    ? new Date(startDate) 
    : (startDate.toDate ? startDate.toDate() : new Date(startDate));
  
  // Sao chép ngày để tránh sửa đổi ngày gốc
  const dueDate = new Date(start);
  
  // Thêm khoảng thời gian (số ngày)
  dueDate.setDate(dueDate.getDate() + (parseInt(duration) || 0));
  
  return dueDate;
};

// Hàm kiểm tra công việc có quá hạn không
const isTaskOverdue = (task) => {
  if (task.status === 'completed') return false;
  
  const dueDate = calculateDueDate(task.start_date, task.duration);
  const today = new Date();
  
  // Loại bỏ phần thời gian để chỉ so sánh ngày
  today.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);
  
  return dueDate < today;
};

if (loading) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md">
        <div className="flex items-center space-x-4">
          <div className="w-8 h-8 border-4 border-emerald-500 dark:border-emerald-400 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-700 dark:text-gray-300">Đang tải công việc...</p>
        </div>
      </div>
    </div>
  );
}

if (error) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md text-center">
        <div className="text-red-500 dark:text-red-400 mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-2">Lỗi Khi Tải Công Việc</h2>
        <p className="text-gray-600 dark:text-gray-400">{error}</p>
      </div>
    </div>
  );
}

return (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
    {/* Thông báo Modal */}
    {notification.show && (
      <div className="fixed top-4 right-4 z-[9999] animate-slide-in-right">
        <div className={`p-4 rounded-lg shadow-lg max-w-md ${
          notification.type === 'error' ? 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100' :
          notification.type === 'warning' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100' :
          notification.type === 'success' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-800 dark:text-emerald-100' :
          'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100'
        }`}>
          <div className="flex items-start">
            <div className="flex-shrink-0 mr-3">
              {notification.type === 'error' && (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {notification.type === 'warning' && (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {notification.type === 'success' && (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              )}
              {notification.type === 'info' && (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{notification.message}</p>
            </div>
            <div className="ml-4">
              <button
                onClick={closeNotification}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    
    {/* Nội Dung Chính */}
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex space-x-8">
        {/* Thanh Bên Trái */}
        <div className="w-64 flex-shrink-0">
      <div className="sticky top-20"> {/* top-20 để dành không gian cho Header cố định ở trên cùng */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/50 max-h-[calc(100vh-5rem)] overflow-y-auto">
            <div className="p-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Trạng thái</h2>
              <div className="mt-4 space-y-2">
                {statusFilters.map((filter) => (
                  <button
                    key={filter.value}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm cursor-pointer whitespace-nowrap ${
                    selectedStatus === filter.value 
                      ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' 
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                    onClick={() => setSelectedStatus(filter.value)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(filter.value)}
                  >
                    <div className="flex items-center justify-between">
                      <span>{filter.label}</span>
                    <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full text-xs">
                      {tasks.filter((task) => (filter.value === 'all' ? true : 
                        filter.value === 'overdue' ? isTaskOverdue(task) : task.status === filter.value)).length}
                      </span>
                    </div>
                  </button>
                ))}
            </div>
              </div>
            </div>
          </div>
        </div>
        {/* Chế Độ Xem Timeline */}
        <div className="flex-1">
<div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/50">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
      <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Dòng Thời Gian Công Việc</h2>
                <div className="flex items-center space-x-4">
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-gray-100"
                    placeholder="Tìm kiếm công việc..."
                  />
        <button 
          className="!rounded-button px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer whitespace-nowrap flex items-center"
          onClick={() => setShowFilterModal(true)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
                    Lọc
          {Object.values(filterOptions).some(value => value !== 'all') && (
            <span className="ml-1 bg-emerald-500 dark:bg-emerald-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
              {Object.values(filterOptions).filter(value => value !== 'all').length}
            </span>
          )}
                  </button>
        <button 
          className="!rounded-button px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer whitespace-nowrap flex items-center"
          onClick={() => setShowSortModal(true)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
          </svg>
          Sắp xếp theo: {sortOption.charAt(0).toUpperCase() + sortOption.slice(1)}
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ml-1 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
                  </button>
                </div>
              </div>
              
              {tasks.length === 0 ? (
      <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
        <p className="mt-4 text-gray-500 dark:text-gray-400 text-center">Không tìm thấy công việc nào. Tạo một công việc mới để bắt đầu.</p>
                </div>
    ) : sortedAndFilteredTasks.length === 0 ? (
                <div className="py-8 text-center">
        <p className="text-gray-500 dark:text-gray-400">Không có công việc nào phù hợp với tiêu chí lọc của bạn.</p>
        <button
          onClick={resetFilters}
          className="mt-2 px-4 py-2 bg-emerald-500 dark:bg-emerald-600 text-white rounded-lg hover:bg-emerald-600 dark:hover:bg-emerald-700 transition-colors"
        >
          Đặt Lại Bộ Lọc
        </button>
                </div>
              ) : (
                <div className="space-y-4">
        {sortedAndFilteredTasks.map((task) => (
                    <div
                      key={task.id}
            className="flex items-center p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md dark:hover:shadow-gray-700/30 transition-shadow cursor-pointer bg-white dark:bg-gray-800"
                      draggable
                      onDragStart={() => handleDragStart(task)}
                      onClick={(e) => openTaskDetails(task, e)}
                    >
                      <div className={`w-1 h-16 ${task.bgColor} rounded-full mr-4`}></div>
                      <div className="flex-1">
              <div className="flex items-center">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{task.text}</h3>
                {isTaskOverdue(task) && (
                  <span className="ml-2 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 text-xs rounded-full">
                    Quá hạn
                  </span>
                )}
                {/* Vai trò của bạn trong công việc này */}
                {currentUser && (() => {
                  const role = getUserRoleInTask(task, currentUser);
                  if (!role) return null;
                  let color = 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
                  let icon = 'fas fa-user-tag';
                  if (role.includes('phê duyệt')) { color = 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200'; icon = 'fas fa-user-check'; }
                  else if (role.includes('giám sát')) { color = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200'; icon = 'fas fa-user-shield'; }
                  else if (role.includes('phối hợp')) { color = 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200'; icon = 'fas fa-user-friends'; }
                  else if (role.includes('thực hiện')) { color = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'; icon = 'fas fa-user-check'; }
                  return (
                    <div className={`inline-flex items-center px-2 py-0.5 ml-2 rounded-full text-xs font-semibold ${color}`}
                      title="Vai trò của bạn trong công việc này">
                                           <i className={`${icon} mr-1`}></i>
                      {role}
                    </div>
                  );
                })()}
              </div>
                        {task.description && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 whitespace-pre-line leading-relaxed">
                  {task.description}
                </p>
                        )}
              <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                <span>Bắt đầu {formatDate(task.start_date)}</span>
                          <span>•</span>
                          <span className={`${
                    task.priority === 'high' ? 'text-red-600 dark:text-red-400' : 
                    task.priority === 'medium' ? 'text-yellow-600 dark:text-yellow-400' : 
                    'text-emerald-600 dark:text-emerald-400'
                            }`}>
                            Ưu tiên {task.priority === 'high' ? 'Cao' : task.priority === 'medium' ? 'Trung bình' : 'Thấp'}
                          </span>
                          {task.progress > 0 && (
                            <>
                              <span>•</span>
                    <span className="text-blue-600 dark:text-blue-400">Hoàn thành {task.progress}%</span>
                            </>
                          )}
                        </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                <span className="font-medium">Đến hạn:</span> {formatDate(calculateDueDate(task.start_date, task.duration))}
                      </div>
            </div>
            <div className="flex items-center">
                      {task.assignees && task.assignees.length > 0 && (
                <div className="flex -space-x-2 mr-3">
                          {task.assignees.map((assignee, index) => (
                            <div 
                              key={`${assignee.uid || 'assignee'}-${index}`} 
                      className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center border border-white dark:border-gray-800"
                              title={assignee.displayName}
                            >
                              {assignee.displayName.charAt(0)}
                            </div>
                          ))}
                        </div>
                      )}
              {canEditTasks && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTask(task);
                      setEditedTask({...task});
                      setShowTaskDetails(true);
                      setIsEditing(true);
                    }}
                    className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-full transition-colors"
                    title="Chỉnh Sửa Công Việc"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const taskToDelete = task;
                      setSelectedTask(taskToDelete);
                      setTaskToDelete(taskToDelete);
                      setShowDeleteConfirm(true);
                    }}
                    className="p-2 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-full transition-colors ml-1"
                    title="Xóa Công Việc"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </>
              )}
            </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>

    {/* Chi Tiết Công Việc Modal */}
    {showTaskDetails && selectedTask && (
<div className="fixed inset-0 bg-gray-500 dark:bg-gray-900/75 bg-opacity-75 flex items-center justify-center z-50">
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:shadow-gray-900/50 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center">
                <div className={`w-2 h-12 ${selectedTask.bgColor} rounded-full mr-4`}></div>
          {isEditing ? (
            <input
              type="text"
              name="text"
              value={editedTask.text}
              onChange={handleInputChange}
              className="text-xl font-bold text-gray-900 dark:text-gray-100 border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-emerald-500 w-full bg-transparent"
            />
          ) : (
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{selectedTask.text}</h2>
          )}
              </div>
              <button
                onClick={closeTaskDetails}
          className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 cursor-pointer"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Action buttons row - New! */}
            {!isEditing && (
              <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 dark:border-gray-700 pb-4">
                {/* Upload New File button */}
                {isTaskAssignee() && (
                  <label className="px-3 py-2 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white rounded-md transition-colors cursor-pointer flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Tải lên tệp mới
                    <input 
                      type="file" 
                      className="hidden" 
                      onChange={handleFileUpload}
                      disabled={isUploading}
                    />
                  </label>
                )}

                {/* Comment button */}
                <button 
                  onClick={() => document.getElementById('comment-input')?.focus()}
                  className="px-3 py-2 bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 text-white rounded-md transition-colors cursor-pointer flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  Gửi bình luận
                </button>

                {/* Complete Task button - only for assignees or managers */}
                {selectedTask.status !== 'completed' && canUpdateStatusAndProgress() && (
                  <button 
                    onClick={() => {
                      // Chỉ cập nhật trạng thái và progress vào form, không gọi handleSaveChanges
                      const updatedTask = {
                        ...selectedTask,
                        status: 'completed',
                        completedAt: new Date(),
                        progress: 100
                      };
                      setEditedTask(updatedTask);
                      setIsEditing(true); // Bật chế độ chỉnh sửa để hiện nút Lưu
                    }}
                    className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-700 text-white rounded-md transition-colors flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Hoàn thành
                  </button>
                )}

                {/* Edit button */}
                {canEditTask() && (
                  <button 
                    onClick={handleEditTask}
                    className="px-3 py-2 bg-amber-500 hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-700 text-white rounded-md transition-colors flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Chỉnh sửa
                  </button>
                )}

                {/* Delete button */}
                {canEditTask() && (
                  <button 
                    onClick={handleDeleteTask}
                    className="px-3 py-2 bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white rounded-md transition-colors flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Xóa
                  </button>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="col-span-2">
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mô tả</h3>
            {isEditing ? (
              <textarea
                name="description"
                value={editedTask.description || ""}
                onChange={handleInputChange}
                className="w-full h-32 p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-800 dark:text-gray-100"
                placeholder="Thêm mô tả..."
              />
            ) : (
              <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{linkifyText(selectedTask.description) || "Không có mô tả."}</p>
            )}
                </div>

                  <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Người phụ trách</h3>
            {isEditing ? (
              <div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {editedTask.assignees && editedTask.assignees.map((assignee, index) => (
                    <div 
                      key={`${assignee.uid || 'assignee'}-${index}`} 
                      className="flex items-center bg-emerald-50 dark:bg-emerald-900/30 p-2 rounded-lg"
                    >
                      <div className="w-6 h-6 rounded-full bg-emerald-200 dark:bg-emerald-700 flex items-center justify-center mr-2">
                        {assignee.displayName.charAt(0)}
                      </div>
                      <span className="text-sm text-emerald-800 dark:text-emerald-200">{assignee.displayName}</span>
                      <button
                        onClick={() => {
                          const updatedAssignees = [...editedTask.assignees];
                          updatedAssignees.splice(index, 1);
                          setEditedTask(prev => ({
                            ...prev,
                            assignees: updatedAssignees
                          }));
                        }}
                        className="ml-2 text-emerald-500 dark:text-emerald-300 hover:text-emerald-700 dark:hover:text-emerald-400"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setShowAssigneeSelector(true)}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Thêm Người Phụ Trách
                </button>
              </div>
            ) : (
                    <div className="space-y-2">
                {selectedTask.assignees && selectedTask.assignees.length > 0 ? (
                  selectedTask.assignees.map((assignee, index) => (
                        <div 
                          key={`${assignee.uid || 'assignee'}-${index}`} 
                      className="flex items-center bg-gray-50 dark:bg-gray-700 p-2 rounded-lg"
                        >
                      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center mr-3">
                            {assignee.displayName.charAt(0)}
                          </div>
                          <div>
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{assignee.displayName}
                          {/* Nếu là currentUser thì hiển thị vai trò */}
                          {currentUser && assignee.uid === currentUser.uid && (
                            <span className="ml-2 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 text-xs font-semibold">
                              {getUserRoleInTask(selectedTask, currentUser)}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{assignee.email}</p>
                          </div>
                        </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Không có người phụ trách</p>
                )}
                  </div>
                )}
          </div>
              </div>

              <div className="space-y-6">
                <div>
  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Trạng thái</h3>
  {isEditing ? (
    <select
      name="status"
      value={editedTask.status || 'notStarted'}
      onChange={handleInputChange}
      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-white"
    >
       <option value="notStarted">Chưa bắt đầu</option>
      <option value="inProgress">Đang thực hiện</option>
      <option value="pendingApproval">Chờ phê duyệt</option>
      <option value="approved">Đã phê duyệt</option>
      <option value="completed">Hoàn thành</option>
      <option value="canceled">Đã hủy</option>
    </select>
  ) : (
    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 flex items-center">
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(selectedTask.status || 'notStarted')}`}>
        {getStatusLabel(selectedTask.status || 'notStarted')}
                    </span>
      {isTaskOverdue(selectedTask) && (
        <span className="ml-2 px-2 py-0.5 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 text-xs rounded-full">
          Quá hạn
        </span>
      )}
                  </div>
  )}
                </div>

                <div>
  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ưu tiên</h3>
  {isEditing ? (
    <div className="flex space-x-2">
      {['low', 'medium', 'high'].map(priority => (
        <button
          key={priority}
          type="button"
          onClick={() => handlePriorityChange(priority)}
          className={`px-3 py-1 rounded-lg text-sm ${
            editedTask.priority === priority
              ? priority === 'high' 
                ? 'bg-red-500 text-white'
                : priority === 'medium'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-blue-500 text-white'
              : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-200'
          }`}
        >
          {priority === 'high' ? 'Cao' : priority === 'medium' ? 'Trung bình' : 'Thấp'}
        </button>
      ))}
    </div>
  ) : (
                  <div className={`rounded-lg p-2 ${
                    selectedTask.priority === 'high' 
        ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-200' 
                      : selectedTask.priority === 'medium'
          ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-200'
          : 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200'
                  }`}>
                    <span className="text-sm font-medium">
                      {selectedTask.priority === 'high' ? 'Cao' : selectedTask.priority === 'medium' ? 'Trung bình' : 'Thấp'}
                    </span>
                  </div>
  )}
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
  <div>
    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ngày Bắt Đầu</h3>
    <p className="text-gray-800 dark:text-gray-200">
      {formatDate(selectedTask.start_date)}
    </p>
  </div>
  <div>
    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ngày Đến Hạn</h3>
    <p className="text-gray-800 dark:text-gray-200">
      {formatDate(calculateDueDate(selectedTask.start_date, selectedTask.duration))}
    </p>
  </div>
</div>

<div>
  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Thời Hạn</h3>
  {isEditing ? (
    <input
      type="number"
      name="duration"
      min="1"
      value={editedTask.duration}
      onChange={handleInputChange}
      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-white"
    />
  ) : (
    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
      <span className="text-sm text-gray-800 dark:text-gray-200">{selectedTask.duration} ngày</span>
    </div>
  )}
</div>

<div>
  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Loại</h3>
  {isEditing ? (
    <select
      name="type"
      value={editedTask.type}
      onChange={handleInputChange}
      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-white"
    >
      <option value="task">Công việc</option>
      <option value="milestone">Cột mốc</option>
      <option value="feature">Tính năng</option>
      <option value="bug">Lỗi</option>
    </select>
  ) : (
    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
      <span className="text-sm text-gray-800 dark:text-gray-200">
        {selectedTask.type === 'task' ? 'Công việc' : 
         selectedTask.type === 'milestone' ? 'Cột mốc' : 
         selectedTask.type === 'feature' ? 'Tính năng' : 'Lỗi'}
      </span>
    </div>
  )}
</div>


<div>
  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tiến Độ</h3>
  {isEditing ? (
    <div>
      <input
        type="range"
        name="progress"
        min="0"
        max="100"
        value={editedTask.progress || 0}
        onChange={handleProgressChange}
        className="w-full dark:accent-emerald-500"
      />
      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>0%</span>
        <span>{editedTask.progress || 0}%</span>
        <span>100%</span>
      </div>
    </div>
  ) : (
    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
        <div 
          className="bg-emerald-500 h-2.5 rounded-full" 
          style={{ width: `${selectedTask.progress || 0}%` }}
        ></div>
      </div>
      <p className="text-sm text-right mt-1 text-gray-800 dark:text-gray-200">{selectedTask.progress || 0}%</p>
    </div>
  )}
</div>
</div>
</div>

<div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6">
  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Bình Luận</h3>
  
  {/* Danh sách bình luận */}
  <div className="space-y-4 mb-6 max-h-80 overflow-y-auto">
    {!comments || comments.length === 0 ? (
      <p className="text-gray-500 dark:text-gray-400 text-center py-4">Chưa có bình luận nào. Hãy là người đầu tiên bình luận!</p>
    ) : (
      // Đảm bảo comments là một mảng trước khi gọi map
      (Array.isArray(comments) ? comments : []).map(comment => {
        // Tìm tất cả các trả lời cho bình luận này
        const replies = Array.isArray(comments) ? comments.filter(c => c.replyTo && c.replyTo.id === comment.id) : [];
        const isRootComment = !comment.replyTo;
        
        if (!isRootComment) return null; // Chỉ hiển thị bình luận gốc, các trả lời sẽ hiển thị bên dưới
        
        return (
          <div key={comment.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            {/* Phần đầu bình luận */}
            <div className="flex items-start">
              <div className="w-8 h-8 rounded-full bg-emerald-200 dark:bg-emerald-800 flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-gray-800 dark:text-gray-200">{comment.createdBy?.displayName?.charAt(0) || '?'}</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{comment.createdBy?.displayName || 'Người dùng không xác định'}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{formatDateTime(comment.createdAt)}</p>
                  </div>
                  <button
                    onClick={() => handleReply(comment)}
                    className="text-sm text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300"
                  >
                    Trả lời
                  </button>
                </div>
                <div className="mt-2 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line break-words">
                  {linkifyText(comment.text)}
                </div>
              </div>
            </div>
            
            {/* Danh sách trả lời */}
            {replies.length > 0 && (
              <div className="mt-3 pl-11 space-y-3">
                {replies.map(reply => (
                  <div key={reply.id} className="bg-white dark:bg-gray-600 rounded-lg p-3">
                    <div className="flex items-start">
                      <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center mr-2 flex-shrink-0">
                        <span className="text-gray-800 dark:text-gray-200">{reply.createdBy?.displayName?.charAt(0) || '?'}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{reply.createdBy?.displayName || 'Người dùng không xác định'}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-300">{formatDateTime(reply.createdAt)}</p>
                          </div>
                          <button
                            onClick={() => handleReply(comment)} // Trả lời bình luận gốc, không phải trả lời của trả lời
                            className="text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300"
                          >
                            Trả lời
                          </button>
                        </div>
                        <div className="mt-1 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line break-words">
                          <span className="text-blue-600 dark:text-blue-400 font-medium">
                            @{reply.replyTo?.createdBy?.displayName || 'Người dùng không xác định'}
                          </span>{' '}
                          {linkifyText(reply.text)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })
    )}
  </div>

                
{/* Ô nhập bình luận */}
<div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
  {replyTo && (
    <div className="mb-2 flex items-center justify-between bg-blue-50 dark:bg-blue-900/30 p-2 rounded">
      <p className="text-sm text-blue-700 dark:text-blue-300">
        Đang trả lời <span className="font-medium">@{replyTo.createdBy.displayName}</span>
      </p>
      <button
        onClick={cancelReply}
        className="text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )}
  <div className="flex items-start">
    <div className="w-8 h-8 rounded-full bg-emerald-200 dark:bg-emerald-800 flex items-center justify-center mr-3 flex-shrink-0">
      <span className="text-gray-800 dark:text-gray-200">
        {currentUser?.displayName?.charAt(0) || currentUser?.email?.charAt(0) || '?'}
      </span>
    </div>
    <div className="flex-1">
      <textarea
        id="comment-input"
        value={newComment}
        onChange={(e) => setNewComment(e.target.value)}
        placeholder={replyTo ? `Trả lời ${replyTo.createdBy.displayName}...` : "Thêm bình luận..."}
        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-600 dark:text-white resize-none"
        rows="2"
      />
      <div className="flex justify-end mt-2">
        <button
          onClick={handleAddComment}
          disabled={!newComment.trim() || isSubmittingComment}
          className={`px-4 py-1.5 rounded-lg text-white ${
            !newComment.trim() || isSubmittingComment
              ? 'bg-gray-400 dark:bg-gray-500 cursor-not-allowed'
              : 'bg-emerald-500 hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-700'
          } transition-colors flex items-center`}
        >
          {isSubmittingComment ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Đang đăng...
            </>
          ) : (
            'Gửi bình luận'
          )}
        </button>
      </div>
    </div>
  </div>
</div>
</div>

<div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6">
  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Nhật Ký Hoạt Động</h3>
  
  {!selectedTask.activityLogs || selectedTask.activityLogs.length === 0 ? (
    <p className="text-gray-500 dark:text-gray-400 text-center py-4">Chưa có hoạt động nào được ghi lại.</p>
  ) : (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-700">
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Thời gian</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Người dùng</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Hành động</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Công việc</th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {selectedTask.activityLogs
            .sort((a, b) => {
              // Sắp xếp theo thời gian từ mới đến cũ
              const timeA = a.timestamp instanceof Date ? a.timestamp : new Date(a.timestamp);
              const timeB = b.timestamp instanceof Date ? b.timestamp : new Date(b.timestamp);
              return timeB - timeA;
            })
            .map(log => {
              const logData = ActivityLogItem({ log, defaultTaskName: selectedTask.text || selectedTask.title });
              if (!logData) return null;
              
              return (
                <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-300">{logData.timestamp}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center mr-2">
                        {logData.userPhotoURL ? (
                          <img 
                            src={logData.userPhotoURL} 
                            alt={logData.userDisplayName} 
                            className="h-8 w-8 rounded-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "";
                              e.target.className = "hidden";
                              e.target.parentElement.innerHTML = logData.userDisplayName.charAt(0);
                            }}
                          />
                        ) : (
                          logData.userDisplayName.charAt(0)
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{logData.userDisplayName}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{logData.content}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-300">{logData.taskName}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  )}
</div>

<div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6">
  <div className="flex items-center justify-between mb-4">
    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Tệp Đính Kèm</h3>
  </div>
  
  {uploadError && (
    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-200 rounded-lg">
      {uploadError}
    </div>
  )}
  
  {isUploading && (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-gray-700 dark:text-gray-300">Đang tải lên...</span>
        <span className="text-sm text-gray-700 dark:text-gray-300">{Math.round(uploadProgress)}%</span>
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
        <div 
          className="bg-emerald-500 h-2.5 rounded-full" 
          style={{ width: `${uploadProgress}%` }}
        ></div>
      </div>
    </div>
  )}
  
  {taskFiles.length === 0 ? (
    <div className="py-8 text-center bg-gray-50 dark:bg-gray-700 rounded-lg">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 002-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2v-1.572L16.732 3.732z" />
      </svg>
      <p className="text-gray-500 dark:text-gray-400">Chưa có tệp nào được đính kèm vào công việc này.</p>
      {isTaskAssignee() && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Nhấp vào "Tải Lên Tệp Mới" để thêm tệp đính kèm.</p>
      )}
    </div>
  ) : (
    <div className="space-y-3">
      {taskFiles.map(file => (
        <div key={file.id} className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="mr-3 flex-shrink-0">
            {getFileIcon(file.type)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{file.name}</p>
            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
              <span>{formatFileSize(file.size)}</span>
              <span className="mx-1">•</span>
              <span>Tải lên bởi {file.uploadedBy.displayName}</span>
              <span className="mx-1">•</span>
              <span>{formatDateTime(file.uploadedAt)}</span>
            </div>
          </div>
          <div className="flex items-center ml-4">
            <button 
              onClick={async (e) => {
                e.preventDefault();
                // Tạo một phần tử liên kết tạm thời
                const downloadLink = document.createElement('a');
                try {
                  // Tải tệp dưới dạng blob
                  const response = await fetch(file.url);
                  if (!response.ok) {
                    throw new Error(`Lỗi HTTP! trạng thái: ${response.status}`);
                  }
                  const blob = await response.blob();
                  
                  // Tạo URL đối tượng cho blob
                  const blobUrl = window.URL.createObjectURL(blob);
                  
                  // Đặt thuộc tính cho liên kết
                  downloadLink.href = blobUrl;
                  downloadLink.download = file.name; // Đặt tên tệp mong muốn
                  
                  // Thêm vào body, nhấp chuột và xóa
                  document.body.appendChild(downloadLink);
                  downloadLink.click();
                  document.body.removeChild(downloadLink);
                  
                  // Thu hồi URL đối tượng để giải phóng bộ nhớ
                  window.URL.revokeObjectURL(blobUrl);
                } catch (error) {
                  console.error('Lỗi tải xuống tệp:', error);
                  // Tùy chọn hiển thị thông báo cho người dùng
                  showNotification('Không thể tải xuống tệp. Vui lòng thử lại.', 'error');
                } finally {
                  // Dọn dẹp phần tử liên kết ngay cả khi có lỗi trước appendChild
                  if (downloadLink.parentNode === document.body) {
                      document.body.removeChild(downloadLink);
                  }
                }
              }}
              className="p-2 text-gray-500 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-full transition-colors"
              title="Tải xuống"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </button>
            {(isTaskAssignee() || (currentUser && file.uploadedBy.uid === currentUser.uid)) && (
              <button
                onClick={() => handleDeleteFile(file)}
                className="p-2 text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-full transition-colors ml-1"
                title="Xóa"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  )}
</div>

<div className="flex justify-end mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
  {isEditing ? (
    <>
      <button
        onClick={handleCancelEdit}
        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors mr-3 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
        disabled={isSaving}
      >
        Hủy
      </button>
      <button
        onClick={handleSaveChanges}
        className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors flex items-center dark:bg-emerald-600 dark:hover:bg-emerald-700"
        disabled={isSaving}
      >
        {isSaving ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Đang lưu...
          </>
        ) : (
          'Lưu Thay Đổi'
        )}
      </button>
    </>
  ) : (
    <>
      <button
        onClick={closeTaskDetails}
        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors dark:bg-gray-600 dark:hover:bg-gray-700"
      >
        Đóng
      </button>
    </>
  )}
</div>
          </div>
        </div>
      </div>
    )}
    {/* Modal Tạo Công Việc */}
    {showTaskModal && (
<div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center dark:bg-gray-900 dark:bg-opacity-75">
  <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl dark:bg-gray-800">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Công Việc Sáng Tạo Mới</h2>
              <button
                onClick={() => setShowTaskModal(false)}
          className="text-gray-400 hover:text-gray-500 cursor-pointer dark:text-gray-300 dark:hover:text-gray-200"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            {/* Thêm các trường biểu mẫu của bạn ở đây */}
          </div>
        </div>
      </div>
    )}

{/* Modal Xác Nhận Xóa */}
{showDeleteConfirm && taskToDelete && (
<div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 dark:bg-gray-900 dark:bg-opacity-75">
  <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 animate-fade-in-down dark:bg-gray-800">
    <div className="text-center mb-6">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4 dark:bg-red-900/30">
        <svg className="h-6 w-6 text-red-600 dark:text-red-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-white">Xóa Công Việc</h3>
      <p className="text-sm text-gray-500 dark:text-gray-300">
        Bạn có chắc chắn muốn xóa công việc "{taskToDelete.text}" không? Hành động này không thể hoàn tác.
      </p>
    </div>
    <div className="flex justify-end space-x-3">
      <button
        onClick={cancelDeleteTask}
        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
        disabled={isSaving}
      >
        Hủy
      </button>
      <button
        onClick={confirmDeleteTask}
        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center dark:bg-red-600 dark:hover:bg-red-700"
        disabled={isSaving}
      >
        {isSaving ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Đang xóa...
          </>
        ) : (
          'Xóa Công Việc'
        )}
      </button>
    </div>
  </div>
</div>
)}
    {/* Modal Bộ Lọc */}
{showFilterModal && (
<div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 dark:bg-gray-900 dark:bg-opacity-75">
  <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 dark:bg-gray-800">
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Lọc Công Việc</h3>
      <button
        onClick={() => setShowFilterModal(false)}
        className="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
    
    <div className="space-y-4">
      {/* Bộ lọc ưu tiên */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Ưu tiên</label>
        <select
          value={filterOptions.priority}
          onChange={(e) => handleFilterChange('priority', e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-emerald-600"
        >
          <option value="all">Tất cả ưu tiên</option>
          <option value="high">Cao</option>
          <option value="medium">Trung bình</option>
          <option value="low">Thấp</option>
        </select>
      </div>
      
      {/* Bộ lọc người phụ trách */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Người phụ trách</label>
        <select
          value={filterOptions.assignee}
          onChange={(e) => handleFilterChange('assignee', e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-emerald-600"
        >
          <option value="all">Tất cả người phụ trách</option>
          {/* Có thể tạo danh sách thành viên phòng ban động ở đây */}
          {currentUser && <option value={currentUser.uid}>Được giao cho tôi</option>}
        </select>
      </div>
      
      {/* Bộ lọc ngày đến hạn */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Ngày đến hạn</label>
        <select
          value={filterOptions.dueDate}
          onChange={(e) => handleFilterChange('dueDate', e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-emerald-600"
        >
          <option value="all">Tất cả ngày</option>
          <option value="overdue">Quá hạn</option>
          <option value="today">Đến hạn hôm nay</option>
          <option value="thisWeek">Đến hạn tuần này</option>
        </select>
      </div>
      
      {/* Bộ lọc tiến độ */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Tiến độ</label>
        <select
          value={filterOptions.progress}
          onChange={(e) => handleFilterChange('progress', e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-emerald-600"
        >
          <option value="all">Tất cả tiến độ</option>
          <option value="notStarted">Chưa bắt đầu (0%)</option>
          <option value="inProgress">Đang thực hiện (1-99%)</option>
          <option value="completed">Hoàn thành (100%)</option>
        </select>
      </div>
    </div>
    
    <div className="mt-6 flex justify-end space-x-3">
      <button
        onClick={resetFilters}
        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
      >
        Đặt lại
      </button>
      <button
        onClick={() => setShowFilterModal(false)}
        className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors dark:bg-emerald-600 dark:hover:bg-emerald-700"
      >
        Áp dụng bộ lọc
      </button>
    </div>
  </div>
</div>
)}
{/* Modal Sắp Xếp */}
{showSortModal && (
<div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 dark:bg-gray-900 dark:bg-opacity-75">
  <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 dark:bg-gray-800">
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Sắp Xếp Công Việc</h3>
      <button
        onClick={() => setShowSortModal(false)}
        className="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
    
    <div className="space-y-2">
      <button
        onClick={() => handleSortChange('dueDate')}
        className={`w-full flex justify-between items-center p-3 rounded-md ${
          sortOption === 'dueDate' 
            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' 
            : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
        }`}
      >
        <span>Ngày đến hạn</span>
        {sortOption === 'dueDate' && (
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        )}
      </button>
      
      <button
        onClick={() => handleSortChange('priority')}
        className={`w-full flex justify-between items-center p-3 rounded-md ${
          sortOption === 'priority' 
            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' 
            : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
        }`}
      >
        <span>Ưu tiên</span>
        {sortOption === 'priority' && (
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        )}
      </button>
      
      <button
        onClick={() => handleSortChange('progress')}
        className={`w-full flex justify-between items-center p-3 rounded-md ${
          sortOption === 'progress' 
            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' 
            : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
        }`}
      >
        <span>Tiến độ</span>
        {sortOption === 'progress' && (
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        )}
      </button>
      
      <button
        onClick={() => handleSortChange('title')}
        className={`w-full flex justify-between items-center p-3 rounded-md ${
          sortOption === 'title' 
            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' 
            : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
        }`}
      >
        <span>Tiêu đề</span>
        {sortOption === 'title' && (
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        )}
      </button>
    </div>
    
    <div className="mt-6 flex justify-end">
      <button
        onClick={() => setShowSortModal(false)}
        className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors dark:bg-emerald-600 dark:hover:bg-emerald-700"
      >
        Áp Dụng Sắp Xếp
      </button>
    </div>
  </div>
</div>
)}

{/* Modal Chọn Người Phụ Trách */}
{showAssigneeSelector && (
<div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 dark:bg-gray-900 dark:bg-opacity-75">
  <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 dark:bg-gray-800">
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Chọn Người Phụ Trách</h3>
      <button
        onClick={() => setShowAssigneeSelector(false)}
        className="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
    
    <div className="max-h-60 overflow-y-auto mb-4">
      {teamMembers.length > 0 ? (
        <div className="space-y-2">
          {teamMembers.map(member => {
            const isSelected = selectedAssignees.some(assignee => assignee.uid === member.uid);
            return (
              <div 
                key={member.uid} 
                className={`flex items-center p-2 rounded-lg cursor-pointer ${
                  isSelected 
                    ? 'bg-emerald-50 dark:bg-emerald-900/30' 
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
                onClick={() => handleAssigneeChange(member)}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                  isSelected 
                    ? 'bg-emerald-200 text-emerald-800 dark:bg-emerald-700 dark:text-emerald-100' 
                    : 'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-100'
                }`}>
                  {member.displayName.charAt(0)}
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${
                    isSelected 
                      ? 'text-emerald-800 dark:text-emerald-300' 
                      : 'text-gray-800 dark:text-gray-200'
                  }`}>
                    {member.displayName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{member.email}</p>
                </div>
                {isSelected && (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-500 dark:text-emerald-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586l-3.293-3.293a1 1 0 111.414-1.414l4 4a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-center text-gray-500 dark:text-gray-400 py-4">Không tìm thấy thành viên phòng ban</p>
      )}
    </div>
    
    <div className="flex justify-end space-x-3">
      <button
        onClick={() => setShowAssigneeSelector(false)}
        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
      >
        Hủy
      </button>
      <button
        onClick={saveAssignees}
        className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors dark:bg-emerald-600 dark:hover:bg-emerald-700"
      >
        Lưu
     
      </button>
    </div>
  </div>
</div>
)}

{/* Modal Xác Nhận Xóa Tệp */}
{showFileDeleteConfirm && fileToDelete && (
<div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 dark:bg-gray-900 dark:bg-opacity-75">
  <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 animate-fade-in-down dark:bg-gray-800">
    <div className="text-center mb-6">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4 dark:bg-red-900/30">
        <svg className="h-6 w-6 text-red-600 dark:text-red-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-white">Xóa Tệp</h3>
      <p className="text-sm text-gray-500 dark:text-gray-300">
        Bạn có chắc chắn muốn xóa tệp "{fileToDelete.name}" không? Hành động này không thể hoàn tác.
      </p>
    </div>
    <div className="flex justify-end space-x-3">
      <button
        onClick={cancelFileDelete}
        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
        disabled={isSaving}
      >
        Hủy
      </button>
      <button
        onClick={confirmDeleteFile}
        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center dark:bg-red-600 dark:hover:bg-red-700"
        disabled={isSaving}
      >
        {isSaving ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Đang xóa...
          </>
        ) : (
          'Xóa Tệp'
        )}
      </button>
    </div>
  </div>
</div>
)}
    {/* Modal CSS cho hiệu ứng với Tailwind CSS */}
    <style>
      {`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out forwards;
        }
      `}
    </style>
  </div>
);
};

// Thêm CSS cho hiệu ứng với Tailwind CSS
const styles = document.createElement('style');
styles.innerHTML = `
@keyframes slideInRight {
   from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.animate-slide-in-right {
  animation: slideInRight 0.3s ease-out forwards;
}
`;
document.head.appendChild(styles);

export default Timeline;